import React, { useEffect, useState } from 'react'
import to from 'await-catch'
import styled from 'styled-components'
import { ButtonWrapper } from '../../Appointments/confirmAppointment/ConfirmAppointment'
import { Button } from '../../../../components/ui/button'
import Feedback from '../../../../components/feedback'
import useIsMobile from '../../../../hooks/useIsMobile'
import { Calendar } from '../../../../assets/icons'
import theme from '../../../../styled/theme'
import moment from 'moment'
import config from '../../../../config'
import { formatDetailText } from '../../../../utils/stringsUtils'
import { formatAppointmentDate } from '../../../../utils/formatDate'
import AddToCalendarQuickAccessModal from '../../../../components/AddToCalendarQuickAccessModal'
import CancelAppointmentConfirmModal from '../../../../components/CancelAppointmentConfirmModal'
import { buildAppointmentCalendarEvent } from '../../../../utils/appointmentCalendarEvent'
import { cancelAppointment, getClinicsDetailed } from '../../../../api/appointments'
import { useHistory, useLocation } from 'react-router-dom'

const EMPTY_VALUE = '-'
const CANCEL_THRESHOLD = config.cancelThresholdHours

const AppointmentsDetail = () => {
	const history = useHistory()
	const location = useLocation()
	const appointment = location?.state?.appointment || null
	const canCancel =
		appointment?.fechaTurnoDateTime &&
		moment(appointment.fechaTurnoDateTime).diff(moment(), 'hours', true) > CANCEL_THRESHOLD
	const isMobile = useIsMobile()
	const [showModal, setShowModal] = useState(false)
	const [showFeedback, setShowFeedback] = useState({ show: false, success: false })

	const [showCancelModal, setShowCancelModal] = useState(false)
	const [clinics, setClinics] = useState([])
	const calendarEvent = buildAppointmentCalendarEvent(appointment)

	const confirmCancelAppointment = async () => {
		if (!appointment || !canCancel) {
			setShowCancelModal(false)
			return
		}

		const appointmentId = appointment.idTurno
		const clinicCode = appointment.codigoEmpresa
		const [err] = await to(cancelAppointment(appointmentId, clinicCode, 1))
		if (!err) {
			setShowCancelModal(false)
			setShowFeedback({ show: true, success: true })
		} else {
			setShowCancelModal(false)
			setShowFeedback({ show: true, success: false })
		}
	}

	useEffect(() => {
		const fetchClinics = async () => {
			const { data } = await getClinicsDetailed()
			if (data) setClinics(data)
		}
		fetchClinics()
	}, [])

	return (
		<>
			<CancelAppointmentConfirmModal
				isOpen={showCancelModal}
				onClose={() => setShowCancelModal(false)}
				onConfirm={confirmCancelAppointment}
				isFullHeight={isMobile}
			/>
			<AddToCalendarQuickAccessModal
				isOpen={showModal}
				onClose={() => setShowModal(false)}
				isFullHeight={isMobile}
				event={calendarEvent}
			/>
			{showFeedback.show ? (
				<Feedback
					title={
						showFeedback.success ? 'Tu turno fue cancelado con éxito' : 'Error al cancelar el turno'
					}
					bodyText={
						showFeedback.success
							? undefined
							: 'Hubo un error al cancelar el turno. Por favor, inténtelo de nuevo.'
					}
					buttonLabel={'Volver al inicio'}
					onClickButton={() => history.push('/inicio')}
					isSuccess={showFeedback.success}
					isError={!showFeedback.success}
				/>
			) : (
				<Container>
					<Content>
						<ContentWrapper>
							<Header>
								<Title>Detalle del turno</Title>
							</Header>
							<DetailsGrid>
								<DetailItem>
									<DetailLabel>Profesional</DetailLabel>
									<DetailValue>
										{formatDetailText(appointment?.nombreProfesional, EMPTY_VALUE)}
									</DetailValue>
								</DetailItem>
								<DetailItem>
									<DetailLabel>Especialidad</DetailLabel>
									<DetailValue>
										{formatDetailText(
											appointment?.nombreEspecialidad || appointment?.descripcion,
											EMPTY_VALUE
										)}
									</DetailValue>
								</DetailItem>
								<DetailItem>
									<DetailLabel>Fecha</DetailLabel>
									<DetailValue>{formatAppointmentDate(appointment, EMPTY_VALUE)}</DetailValue>
								</DetailItem>
								<DetailItem>
									<DetailLabel>Dirección</DetailLabel>
									<DetailValue>
										{clinics.find(c => c.codigoEmpresa === appointment?.codigoEmpresa)?.direccion ||
											EMPTY_VALUE}
									</DetailValue>
								</DetailItem>
							</DetailsGrid>
						</ContentWrapper>
						<ButtonWrapper>
							<Button
								iconLeft={<Calendar color={theme.colors.brand.blueOmint} />}
								$showBorder={false}
								$mt={'0px'}
								label={'Agregar a calendario'}
								$variant="secondary"
								size={isMobile ? 'mobile' : 'xlarge'}
								$underline={true}
								onClick={() => setShowModal(true)}
							/>
							{canCancel && (
								<Button
								$showBorder={false}
								$mt={'0px'}
								label={'Cancelar turno'}
								$variant="tertiary"
								size={isMobile ? 'mobile' : 'xlarge'}
								$underline={true}
								onClick={() => setShowCancelModal(true)}
							/>
							)}
					
						</ButtonWrapper>
					</Content>
				</Container>
			)}
		</>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 80vh;
	width: 80vw;
	padding: 20px;
	justify-content: space-between;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		display: flex;
		flex-direction: column;
		height: 80vh;
		width: 100%;
		padding: 16px 0px;
	}
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	
	}
`

const Header = styled.div`
	display: flex;
	align-items: flex-start;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 24px;
`

const Title = styled.h1`
	font-size: 28px;
	font-weight: 600;
	margin: 0;
	color: #333;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		font-size: 24px;
	}
`

const ContentWrapper = styled.div`
	margin-bottom: 24px;
`

const DetailsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 28px;
	padding-bottom: 24px;
	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		gap: 16px;
	}
`

const DetailItem = styled.div`
	margin-top: auto;
	gap: 8px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		margin-top: 16px;
	}
`

const DetailLabel = styled.label`
	font-size: 13px;
	font-weight: 600;
	color: #333;
	text-transform: none;
`

const DetailValue = styled.p`
	font-size: 16px;
	color: #666;
	margin: 0;
	font-weight: 400;
`

export default AppointmentsDetail
